import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BanRemoveDialog } from "../pages/administration/BanDialogs";
import { banId, banText, getBanRemoveRequest, readBanResponse, type BanRecord } from "../pages/administration/banData";
import { sendBanMutation } from "../pages/administration/banActions";
import type { useBanStatus } from "./useBanStatus";
import { GD } from "src/GD";

export const BanStatusPanel = ({ status, projectId, userId, disabled }: { status: ReturnType<typeof useBanStatus>; projectId: number; userId: number; disabled: boolean }) => {
	useLocale();
	const { t } = useTranslation();
	const [selected, setSelected] = useState<BanRecord | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const alive = useRef(true);
	const pending = useRef<AbortController | null>(null);
	useEffect(() => {
		alive.current = true;
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => { alive.current = false; pending.current?.abort(); setSelected(null); });
		return () => { alive.current = false; pending.current?.abort(); GD.S_AUTH_LOG_OUT.unsubscribe(logout); };
	}, []);
	const remove = async () => {
		if (!selected || pending.current || !alive.current) return;
		const controller = new AbortController(); pending.current = controller; setBusy(true); setError("");
		try {
			const response = readBanResponse(await sendBanMutation(getBanRemoveRequest(projectId, selected.id), userId, projectId, controller.signal));
			if (!alive.current || controller.signal.aborted) return;
			if (response.error) { setError(response.error); return; }
			if (response.data == null || response.data === false) { setError(t("bans.errors.unconfirmed")); return; }
			setSelected(null);
		} catch (error) {
			if (alive.current && !controller.signal.aborted) setError(t(error instanceof Error && error.message === "contextChanged" ? "bans.errors.contextChanged" : "bans.errors.unconfirmed"));
		} finally {
			if (pending.current === controller) pending.current = null;
			if (alive.current && !controller.signal.aborted) setBusy(false);
		}
	};
	return <section className="ban-status" aria-label={t("bans.status.title")} aria-busy={status.busy}>
		<div className="ban-status-head"><strong>{t("bans.status.title")}</strong><button type="button" disabled={disabled || status.busy || busy} onClick={status.refresh}>{t("common.refresh")}</button></div>
		{status.busy && <p role="status">{t("bans.status.checking")}</p>}
		{status.error && <p className="error" role="alert">{t("bans.status.error")}</p>}
		{!status.busy && !status.error && status.snapshot && <>
			{!status.snapshot.rows.length ? <p role="status">{t("bans.status.none")}</p> : <>
				<p className="hint">{t("bans.status.scopeHint")}</p>
				{status.snapshot.rows.map((row, index) => <div className="ban-status-row" key={`${banText(row.id)}:${index}`}>
					<div><strong>{t(`bans.targets.${banText(row.target_type)}`, { defaultValue: banText(row.target_type) })} · #{banText(row.target_id)}</strong><small>{msg("id")}{banText(row.id)}</small>{typeof row.reason === "string" && row.reason && <p>{row.reason}</p>}</div>
					<button type="button" className="danger" disabled={disabled || busy || banId(row.id) === null} onClick={() => { setError(""); setSelected(row); }}>{t("bans.remove")}</button>
				</div>)}
			</>}
		</>}
		{selected && <BanRemoveDialog record={selected} busy={busy} error={error} onClose={() => { if (!pending.current) setSelected(null); }} onRemove={() => void remove()} />}
	</section>;
};
